import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Landmark, Paperclip, MessageSquare, ArrowRight, AlertTriangle, FileDown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useTenant } from "@/context/TenantContext";
import { ANSWER_STATE_LABELS, SUBMISSION_STATUS } from "@/lib/strings";
import { CorrectionsPanel } from "@/components/CorrectionsPanel";
import { MefKpiPanel } from "@/components/MefKpiPanel";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import SubmissionBar from "@/components/SubmissionBar";

// FASE 5 — Riepilogo di compilazione del Questionario MEF – Banche per l'utente
// aziendale: numero ufficiale, stato risposta, evidenze, commenti, richieste del
// consulente. Nessuno scoring né nota riservata al consulente.
function renderAnswer(v) {
  if (v === null || v === undefined || v === "") return null;
  if (v === "UNKNOWN") return "Non so";
  if (v === "NOT_APPLICABLE") return "Non applicabile";
  if (v === true) return "Sì";
  if (v === false) return "No";
  if (typeof v === "object") return JSON.stringify(v);
  const s = String(v);
  return s.length > 140 ? `${s.slice(0, 140)}…` : s;
}

export default function MefRiepilogo() {
  const { companyId, period } = useTenant();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const download = async (path, filename) => {
    try {
      const res = await api.get(`/companies/${companyId}/periods/${period.id}/mef/${path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download non riuscito", { description: formatApiError(e) });
    }
  };

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/${period.id}/mef/compilazione`);
      setData(data);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [companyId, period?.id]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <p className="text-sm text-slate-600" data-testid="mef-riepilogo-loading">Caricamento riepilogo…</p>;
  }
  if (error) {
    return <p className="text-sm text-red-700" data-testid="mef-riepilogo-error">{error}</p>;
  }
  if (!data) return null;

  const { summary, questions, consultant_requests } = data;
  const compileTarget = summary.first_incomplete
    ? `/questionario-mef?domanda=${summary.first_incomplete}`
    : "/questionario-mef";

  return (
    <div className="space-y-6 max-w-4xl" data-testid="mef-riepilogo-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Landmark className="w-7 h-7 text-green-800" aria-hidden="true" />
            Questionario MEF – Banche
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Riepilogo delle {summary.total} domande della sezione "Dialogo con la banca",
            con la numerazione ufficiale del {data.doc_reference?.version_label
              || "Documento PMI-Banche"}.
          </p>
          {["INTEGRATION_REQUIRED", "READY_FOR_RESUBMISSION"].includes(data.effective_status) && (
            <p className="mt-2 text-sm font-semibold text-red-800" data-testid="mef-effective-status">
              {SUBMISSION_STATUS[data.effective_status].label}
              {data.effective_status === "INTEGRATION_REQUIRED"
                ? ` · ${data.questionnaire_summary?.blocking_inconsistencies ?? 0} incongruenze bloccanti`
                : ""}
            </p>
          )}
        </div>
        <Link to={compileTarget}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-green-700 hover:bg-green-800 rounded-md px-4 py-2 transition-colors"
          data-testid="mef-compile-cta">
          {data.read_only && !["INTEGRATION_REQUIRED", "READY_FOR_RESUBMISSION"].includes(data.effective_status)
            ? "Visualizza il questionario"
            : summary.first_incomplete ? "Continua la compilazione" : "Rivedi le domande"}
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Link>
        <div className="flex flex-wrap gap-2 w-full" data-testid="mef-export-actions">
          <button type="button" onClick={() => download("scheda.pdf", `scheda_sostenibilita_banca_${period?.year}.pdf`)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-800 border border-green-300 bg-white hover:bg-green-50 rounded-md px-3 py-1.5"
            data-testid="mef-download-pdf">
            <FileDown className="w-4 h-4" aria-hidden="true" /> Scheda di sostenibilità per la banca (PDF)
          </button>
          <button type="button" onClick={() => download("export.xlsx", `indicatori_mef_vsme_${period?.year}.xlsx`)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-800 border border-green-300 bg-white hover:bg-green-50 rounded-md px-3 py-1.5"
            data-testid="mef-download-xlsx">
            <FileDown className="w-4 h-4" aria-hidden="true" /> Export Excel (MEF + VSME)
          </button>
        </div>
      </div>

      <SubmissionBar />
      <MefKpiPanel refreshToken={summary.valid} />
      <CorrectionsPanel corrections={data.questionnaire_summary?.corrections || []}
        readOnly={data.read_only && !["INTEGRATION_REQUIRED", "READY_FOR_RESUBMISSION"].includes(data.effective_status)}
        testid="mef-corrections" />

      <Card className="border-slate-200" data-testid="mef-riepilogo-summary">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 mb-3" title="Quota di domande con una risposta, sulle domande della sezione Dialogo con la banca.">
            <div className="flex-1 h-3 rounded-full bg-slate-100 border border-slate-200" data-testid="mef-riepilogo-bar">
              <div className="h-full rounded-full bg-green-700 transition-[width]" style={{ width: `${summary.valid_completion_percentage}%` }} />
            </div>
            <span className="text-sm font-bold text-slate-900" data-testid="mef-riepilogo-pct">{summary.valid_completion_percentage}%</span>
          </div>
          <p className="mb-3 text-xs text-slate-600" data-testid="mef-riepilogo-progress-note">
            {summary.valid} valide su {summary.total} domande · {summary.answered} risposte presenti
          </p>
          <div className="flex flex-wrap gap-2 text-xs">
            {Object.entries(summary.states || {}).filter(([, n]) => n > 0).map(([state, n]) => (
              <Badge key={state} variant="outline" className={ANSWER_STATE_LABELS[state]?.cls || ""}
                data-testid={`mef-state-count-${state.toLowerCase()}`}>
                {ANSWER_STATE_LABELS[state]?.label || state}: {n}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {consultant_requests?.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="mef-consultant-requests">
          <p className="text-sm font-semibold text-amber-900 flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4" aria-hidden="true" />
            Richieste di integrazione del consulente ({consultant_requests.length})
          </p>
          <ul className="space-y-1 text-sm text-amber-900">
            {consultant_requests.map((r) => (
              <li key={r.id} data-testid={`mef-request-${r.id}`}>
                <strong>{r.target_code || r.target_type}</strong>: {r.comment}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Card className="border-slate-200">
        <CardContent className="p-0 divide-y divide-slate-100">
          {questions.map((q) => {
            const meta = ANSWER_STATE_LABELS[q.state] || { label: q.state, cls: "" };
            const val = renderAnswer(q.answer_value);
            return (
              <div key={q.question_code} className="p-4 flex flex-wrap items-start gap-3"
                data-testid={`mef-row-${q.question_code}`}>
                <div className="flex items-center gap-2 w-32 shrink-0">
                  {q.official_indicator_number != null && (
                    <Badge variant="outline" className="bg-sky-50 text-sky-800 border-sky-300 text-[10px]"
                      data-testid={`mef-row-official-${q.question_code}`}
                      title="Numerazione ufficiale del Documento PMI-Banche (dic. 2025)">
                      N. {q.official_indicator_number}
                    </Badge>
                  )}
                  <span className="font-mono text-[11px] text-slate-500">{q.mef_code}</span>
                </div>
                <div className="flex-1 min-w-[220px]">
                  <p className="text-sm font-medium text-slate-900 leading-snug">{q.text}</p>
                  <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-slate-600">
                    {val !== null && (
                      <span className="font-semibold text-slate-800" data-testid={`mef-row-value-${q.question_code}`}>{val}</span>
                    )}
                    {q.evidence_count > 0 && (
                      <span className="inline-flex items-center gap-1" data-testid={`mef-row-evidence-${q.question_code}`}>
                        <Paperclip className="w-3 h-3" aria-hidden="true" /> {q.evidence_count} documento/i
                      </span>
                    )}
                    {q.has_comment && (
                      <span className="inline-flex items-center gap-1" data-testid={`mef-row-comment-${q.question_code}`}>
                        <MessageSquare className="w-3 h-3" aria-hidden="true" /> Commento presente
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <Badge variant="outline" className={meta.cls}
                    data-testid={`mef-row-state-${q.question_code}`}>{meta.label}</Badge>
                  {q.blocking && (
                    <AlertTriangle className="w-4 h-4 text-red-700" aria-label="Incoerenza bloccante"
                      data-testid={`mef-row-blocking-${q.question_code}`} />
                  )}
                  <Link to={`/questionario-mef?domanda=${q.question_code}`}
                    className="text-xs font-semibold text-green-700 hover:underline whitespace-nowrap"
                    data-testid={`mef-goto-${q.question_code}`}>
                    {data.read_only ? "Visualizza domanda" : "Vai alla domanda"}
                  </Link>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
